import { ISearchEvento } from "@/interfaces/change.interface";
import {
  AdicionalFormSchema,
  EventoDto,
  EventoFormSchema,
  HistoricoPerguntasResponse,
  IEvento,
  IEventoRequest,
  ILayoutInscricaoParams,
  PerguntarIAEventoDto,
  PerguntarIAEventoResponse,
  SugestoesResponse,
} from "@/interfaces/evento.interface";
import type {
  MultiIngressosConfig,
  ReservarMultiIngressoResponse,
  UpdateMultiIngressosConfigPayload,
} from "@/interfaces/multi-ingressos.interface";
import {
  IBasePaginatedRequestTipo,
  IEventoPaginatedRequest,
  IPaginatedRequest,
  PaginatedResponse,
} from "@/interfaces/paginated.interface";
import { Completeeventos, configuracaosModel } from "@/interfaces/zod";
import { arrayUniqueByKey, downloadFile } from "@/lib/utils";
import { prepareImageForUpload } from "@/lib/prepare-image-upload";
import { postLog } from "@/lib/logs";
import { skipGlobalErrorToast } from "@/lib/rtk-query-config";
import {
  SetCampos,
  SetCentroCustos,
  SetCredenciais,
  SetEquipes,
  SetEquipesFormulario,
  SetEventoId,
  SetFormasPagamentos,
  SetIgrejas,
  SetReunioes,
  SetProdutos,
  SetGrupos,
} from "@/redux/slices/eventos.slice";
import { setLoginId } from "@/redux/slices/general.slice";
import type { RootState } from "@/redux/store";
import { z } from "zod";
import {
  SetEtiquetas,
  SetEventos,
  SetMensagens,
  SetPadrinhos,
  SetQuartosParticipantes,
  SetQuartosVoluntarios,
} from "./../redux/slices/eventos.slice";
import { createBaseApi } from "./base.service";
import { RESOURCES } from "./resources";

/** Tag RTK Query da listagem `/eventos/datatable` — não usar `localStorage.EventoId` (muda entre fetch e save). */
const EVENTOS_DATATABLE_LIST_TAG_ID = "DATATABLE-LIST";

/** Tag estável da lista do select/home (`getEventos`). Evita mismatch com `localStorage.EventoId` ao trocar evento. */
const SELECT_EVENTOS_LIST_TAG_ID = "LIST";

/** Só espelha no slice se a query ainda for do EventoId selecionado (anti-prefetch/race). */
async function syncSliceIfCurrentEvento(
  eventoId: number,
  queryFulfilled: Promise<{ data: any }>,
  getState: () => unknown,
  dispatch: (action: any) => void,
  action: (data: any) => any,
) {
  const { data } = await queryFulfilled;
  const current = (getState() as RootState).eventosReducer.EventoId;
  if (Number(current) !== Number(eventoId)) return;
  dispatch(action(data));
}

/** Espelha campos do form de cadastro no `IEvento` da store (preview da home). */
function mergeEventoFormIntoListaItem(
  atual: IEvento,
  body: Record<string, unknown>,
  savedId: number,
): IEvento {
  const titulo =
    (typeof body.Descricao === "string" && body.Descricao.trim()
      ? body.Descricao
      : null) ??
    (typeof body.Titulo === "string" && body.Titulo.trim()
      ? body.Titulo
      : null) ??
    atual.Titulo;

  return {
    ...atual,
    Id: savedId,
    Titulo: titulo,
    Status: body.Status != null ? Number(body.Status) : atual.Status,
    StatusEquipe:
      body.StatusEquipe != null ? Number(body.StatusEquipe) : atual.StatusEquipe,
    Valor: body.Valor != null ? Number(body.Valor) : atual.Valor,
    ValorTaxa: body.ValorTaxa != null ? Number(body.ValorTaxa) : atual.ValorTaxa,
    DataEvento:
      (body.DataEvento as IEvento["DataEvento"]) ?? atual.DataEvento,
    DataExtenso:
      (body.DataExtenso as string | undefined) ?? atual.DataExtenso,
    NomeLocal:
      (body.NomeLocal as string | undefined) ?? atual.NomeLocal,
    IdadeMin: body.IdadeMin != null ? Number(body.IdadeMin) : atual.IdadeMin,
    IdadeMax: body.IdadeMax != null ? Number(body.IdadeMax) : atual.IdadeMax,
    Capacidade:
      body.Capacidade != null ? Number(body.Capacidade) : atual.Capacidade,
    GeneroExclusivo:
      body.GeneroExclusivo !== undefined
        ? (body.GeneroExclusivo as IEvento["GeneroExclusivo"])
        : atual.GeneroExclusivo,
    TempoLimiteInscricaoMinutos:
      body.TempoLimiteInscricaoMinutos !== undefined
        ? (body.TempoLimiteInscricaoMinutos as IEvento["TempoLimiteInscricaoMinutos"])
        : atual.TempoLimiteInscricaoMinutos,
  };
}

function syncEventoSalvoNaLista(
  dispatch: (action: unknown) => void,
  getState: () => RootState,
  body: Record<string, unknown>,
  result?: { Id?: number } | null,
) {
  const eventos = getState().eventosReducer.Eventos;
  if (!eventos?.length) return;

  const savedId =
    body.Id != null && Number(body.Id) > 0
      ? Number(body.Id)
      : Number(result?.Id ?? 0);
  if (!savedId || !eventos.some((ev) => ev.Id === savedId)) return;

  dispatch(
    SetEventos(
      eventos.map((ev) =>
        ev.Id === savedId ? mergeEventoFormIntoListaItem(ev, body, savedId) : ev,
      ),
    ),
  );
}

export const eventosApi = createBaseApi<EventoDto>(RESOURCES.EVENTOS);

export const eventosAPI = eventosApi.injectEndpoints({
  endpoints: (build) => ({
    getEventos: build.query<any, IEventoRequest>({
      queryFn: async (
        { roles, page, search, eventoId, configuracaoId, loginId, tipo },
        api,
        extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/eventos`,
          params: {
            roles: Array.isArray(roles) ? roles.join(",") : roles,
            page,
            search,
            configuracaoId,
            loginId,
            eventoId: eventoId ||
              (api.getState() as RootState).eventosReducer.EventoId,
            tipo: Array.isArray(tipo) ? tipo.join(",") : tipo,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const { data } = await queryFulfilled;
        const incoming = Array.isArray(data) ? data : [];
        if (patch.page > 1) {
          const current = (getState() as RootState).eventosReducer.Eventos;
          dispatch(
            SetEventos(
              arrayUniqueByKey(
                (Array.isArray(current) ? current : []).concat(incoming),
                "Id"
              ) as IEvento[]
            )
          );
        } else if (incoming.length === 0) {
          const currentId = (getState() as RootState).eventosReducer.EventoId;
          const rolesValue = String(
            Array.isArray(patch.roles) ? patch.roles.join(",") : (patch.roles ?? ""),
          );
          const keepSelection =
            !rolesValue.includes("Home") &&
            currentId != null &&
            Number(currentId) > 0 &&
            !Number.isNaN(Number(currentId));
          if (keepSelection) return;
          dispatch(SetEventos(incoming));
        } else {
          dispatch(SetEventos(incoming));
        }
      },
      providesTags: () => {
        return [
          {
            type: "SelectEventos",
            id: SELECT_EVENTOS_LIST_TAG_ID,
          },
          {
            type: "SelectEventos",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getEventosConfiguracao: build.query<any, IEventoRequest>({
      queryFn: async (
        { roles, page, search, configuracaoId },
        api,
        extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/eventos`,
          params: {
            roles: Array.isArray(roles) ? roles.join(",") : roles,
            page,
            search,
            configuracaoId,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      providesTags: () => {
        return [
          {
            type: "SelectEventosConfiguracao",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getResumo: build.query<
      any,
      number | { eventoId: number; loginId?: number }
    >({
      query: (arg) => {
        const eventoId = typeof arg === "number" ? arg : arg.eventoId;
        const loginId = typeof arg === "object" ? arg.loginId : undefined;
        return {
          url: `/eventos/resumo/${eventoId}`,
          method: "GET",
          ...(loginId != null && { params: { loginId } }),
        };
      },
      providesTags: (_result, _error, arg) => {
        const eventoId = typeof arg === "number" ? arg : arg.eventoId;
        return [
          {
            type: "ResumoEvento",
            id: String(eventoId),
          },
          { type: "ResumoEvento" },
        ];
      },
    }),
    getEvento: build.query<
      any,
      | number
      | { eventoId: number; loginId?: number; skipSetEventos?: boolean }
    >({
      query: (arg) => {
        const eventoId = typeof arg === "number" ? arg : arg.eventoId;
        const loginId = typeof arg === "object" ? arg.loginId : undefined;
        return {
          url: `/eventos/${eventoId}`,
          method: "GET",
          ...(loginId != null && { params: { loginId } }),
        };
      },
      async onQueryStarted(queryArg, { dispatch, queryFulfilled }) {
        const skipSetEventos =
          typeof queryArg === "object" && queryArg?.skipSetEventos === true;
        if (skipSetEventos) {
          await queryFulfilled;
          return;
        }
        const { data } = await queryFulfilled;
        dispatch(SetEventos([data]));
      },
      extraOptions: skipGlobalErrorToast,
    }),
    getEventoByIdentificador: build.query<any, ILayoutInscricaoParams>({
      query: ({ identificador, login }) => ({
        url: `/eventos/byIdentificador/${login}/${identificador}`,
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "EventosIdentificador",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
      async onQueryStarted(_query, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          console.log("[INSCRICAO_DEBUG] getEventoByIdentificador onQueryStarted, data?.LoginId:", data?.LoginId);

          if (data) {
            // Verificar o LoginId atual na store antes de setar
            const state = getState() as RootState;
            const currentLoginId = state?.generalReducer?.LoginId;

            // Só setar LoginId se for um valor válido E diferente do atual
            if (data?.LoginId != null && data.LoginId > 0 && data.LoginId !== currentLoginId) {
              console.log("[INSCRICAO_DEBUG] Setando LoginId do evento no service:", data.LoginId, "(atual:", currentLoginId, ")");
              dispatch(setLoginId(data.LoginId));
            } else if (data?.LoginId != null && data.LoginId > 0 && data.LoginId === currentLoginId) {
              console.log("[INSCRICAO_DEBUG] LoginId do evento já está correto no service, não setando novamente:", data.LoginId);
            } else {
              console.warn("[INSCRICAO_DEBUG] data.LoginId é inválido (null/undefined/0), não setando:", data?.LoginId);
            }

            dispatch(SetEventos([data]));
            dispatch(SetEventoId(data?.Id));
            if (Array.isArray(data?.Igrejas)) {
              dispatch(SetIgrejas({ Igrejas: data.Igrejas }));
            }
          }
        } catch (error) {
          console.error("[INSCRICAO_DEBUG] Erro no onQueryStarted de getEventoByIdentificador:", error);
        }
      },
    }),
    getCampos: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/campos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "Campos",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetCampos,
        );
      },
    }),
    getEquipesFormulario: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/equipes-formulario/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "EquipesFormularioEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetEquipesFormulario,
        );
      },
    }),
    getCredenciais: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/credenciais/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "CredenciaisEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetCredenciais,
        );
      },
    }),
    getEquipesEventos: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/equipes/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "EquipesEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetEquipes,
        );
      },
    }),
    getCirculos: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/circulos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "CirculosEvento",
            id: String(eventoId),
          },
        ];
      },
    }),
    getCirculosWithDetails: build.query<any, number>({
      query: (eventoId) => ({
        url: `/eventos/circulos/${eventoId}`,
        method: "GET",
        params: {
          select: {
            Cor: true,
            Id: true,
            Titulo: true,
            GrupoWhatsapp: true,
            Capacidade: true,
            CEP: true,
            Logradouro: true,
            Bairro: true,
            Cidade: true,
            Estado: true,
            Numero: true,
            Complemento: true,
            Referencia: true,
            Latitude: true,
            Longitude: true,
            circulodirigentes: {
              select: {
                Id: true,
                equipanteeventos: {
                  select: {
                    equipes: {
                      select: {
                        Id: true,
                        Nome: true,
                      },
                    },
                    equipantes: {
                      select: {
                        Id: true,
                        Nome: true,
                        Apelido: true,
                        Fone: true,
                        Email: true,
                        Latitude: true,
                        Longitude: true,
                      },
                    },
                  },
                },
              },
            },
            circuloparticipantes: {
              select: {
                Id: true,
                participantes: {
                  select: {
                    Id: true,
                    Nome: true,
                    Apelido: true,
                    Fone: true,
                    Email: true,
                    Bairro: true,
                    Cidade: true,
                    casais: {
                      select: {
                        Casal: true,
                      },
                    },
                  },
                },
              },
            },
            _count: {
              select: {
                circuloparticipantes: true,
                circulodirigentes: true,
              },
            },
          },
        },
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "CirculosEvento",
            id: String(eventoId),
          },
        ];
      },
    }),
    getCaronas: build.query<any, number>({
      query: (eventoId) => ({
        url: `/eventos/caronas/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "CaronasEvento",
            id: String(eventoId),
          },
        ];
      },
    }),
    getMensagens: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/mensagens/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "MensagensEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetMensagens,
        );
      },
    }),

    getFormasPagamento: build.query<any, number>({

      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui

      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.

      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/forma-pagamento/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "FormasPagamentoEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetFormasPagamentos,
        );
      },
    }),
    getCentroCustos: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/centro-custos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "CentroCustosEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetCentroCustos,
        );
      },
    }),
    getEtiquetas: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/etiquetas/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "EtiquetasEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetEtiquetas,
        );
      },
    }),
    getIgrejasEvento: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/igrejas/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "IgrejasEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetIgrejas,
        );
      },
    }),

    getPadrinhos: build.query<any, number>({

      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui

      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.

      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/padrinhos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "PadrinhosEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetPadrinhos,
        );
      },
    }),
    getReunioes: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/reunioes/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "ReunioesEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetReunioes,
        );
      },
    }),
    getVoluntarios: build.query<any, ISearchEvento>({
      query: ({ eventoId, search }) => ({
        url: `/eventos/voluntarios/${eventoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "VoluntariosEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getVoluntariosPadrinhos: build.query<any, ISearchEvento>({
      query: ({ eventoId, search }) => ({
        url: `/eventos/voluntarios-padrinhos/${eventoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "VoluntariosEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getVoluntariosDirigente: build.query<any, ISearchEvento>({
      query: ({ eventoId, search }) => ({
        url: `/eventos/voluntarios-dirigente/${eventoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "VoluntariosEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getVoluntariosQuartos: build.query<any, ISearchEvento & { quartoId?: number }>({
      query: ({ eventoId, search, id, quartoId }) => ({
        url: `/eventos/voluntarios-quartos/${eventoId}`,
        params: { search, id, quartoId },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "VoluntariosEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getVoluntariosCaronas: build.query<any, ISearchEvento>({
      query: ({ eventoId, search }) => ({
        url: `/eventos/voluntarios-caronas/${eventoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "VoluntariosEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getQuartosParticipantes: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/quartos/${eventoId}`,
        params: { tipo: 1 },
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "QuartosParticipantesEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetQuartosParticipantes,
        );
      },
    }),
    getQuartosVoluntarios: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/quartos/${eventoId}`,
        params: { tipo: 2 },
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: "QuartosVoluntáriosEvento",
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetQuartosVoluntarios,
        );
      },
    }),
    getEventosPaginated: build.query<
      PaginatedResponse<Completeeventos>,
      IBasePaginatedRequestTipo
    >({
      query: ({ page, perPage, sortingField, sortingDir, search, tipo, emAndamento, apenasConcluidas, cursoId, semestreLetivo, status }) => ({
        url: `/eventos/datatable`,
        params: {
          page,
          perPage,
          sortingDir,
          sortingField,
          search,
          tipo,
          ...(emAndamento !== undefined && { emAndamento }),
          ...(apenasConcluidas !== undefined && { apenasConcluidas }),
          ...(cursoId != null && cursoId !== undefined && { cursoId }),
          ...(semestreLetivo ? { semestreLetivo } : {}),
          ...(status != null && status !== "" && { status }),
        },
        method: "GET",
      }),
      providesTags: () => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
      ],
    }),
    getTurmasDisponiveisPerfil: build.query<
      { data: Array<{ Id: number; Titulo: string; ConfiguracaoId: number; DataConclusao: string; LoginId: number; configuracaos: { CursoId: number | null; Titulo: string } | null }> },
      { email?: string; cursoId?: number; configuracaoId?: number }
    >({
      query: ({ email, cursoId, configuracaoId }) => ({
        url: `/eventos/turmas-disponiveis-perfil`,
        params: {
          ...(email && { email }),
          ...(cursoId != null && { cursoId }),
          ...(configuracaoId != null && { configuracaoId }),
        },
        method: "GET",
      }),
      providesTags: () => [{ type: "Eventos", id: "TURMAS-PERFIL" }],
    }),
    getEventosAgendaPeriodo: build.query<
      {
        Id: number;
        LoginId: number;
        ConfiguracaoId: number;
        Titulo: string | null;
        DataEvento: string | null;
        NomeLocal: string | null;
        TipoEvento: number | null;
        Capacidade: number | null;
        Valor: string | number;
        ValorTaxa: string | number;
        Status: number;
        StatusEquipe: number | null;
        configuracaos: { Titulo: string } | null;
      }[],
      { dtIni: string; dtFim: string }
    >({
      query: ({ dtIni, dtFim }) => ({
        url: `/eventos/agenda-periodo`,
        params: { dtIni, dtFim },
        method: "GET",
      }),
      providesTags: () => [{ type: "Eventos", id: "AGENDA_PERIODO" }],
    }),
    getAdicionaisPaginated: build.query<
      PaginatedResponse<z.infer<typeof AdicionalFormSchema>>,
      IEventoPaginatedRequest
    >({
      query: ({
        eventoId,
        page,
        perPage,
        sortingField,
        sortingDir,
        search,
      }) => ({
        url: `/eventos/${eventoId}/findAllAdicionais`,
        params: {
          page,
          perPage,
          sortingDir,
          sortingField,
          search,
        },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "Adicionais",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getCuponsPaginated: build.query<any, IEventoPaginatedRequest>({
      query: ({ eventoId, page, perPage, sortingField, sortingDir, search }) => ({
        url: `/eventos/${eventoId}/cupons`,
        params: { page, perPage, sortingDir, sortingField, search },
        method: "GET",
      }),
      providesTags: () => [{ type: "Cupons", id: window.localStorage.getItem("EventoId") }],
    }),
    getCuponsResumo: build.query<
      {
        totalCupons: number;
        cuponsAtivos: number;
        totalUtilizacoes: number;
        totalDescontoConcedido: number;
        valorOriginalTotal: number;
        valorRecebidoTotal: number;
        cupomMaisUtilizado: { codigo: string; utilizacoes: number } | null;
        porCupom: Array<{
          cupomId: number;
          codigo: string;
          tipoPublico: number;
          utilizacoes: number;
          limiteUso: number | null;
          descontoTotal: number;
        }>;
      },
      { eventoId: number }
    >({
      query: ({ eventoId }) => ({
        url: `/eventos/${eventoId}/cupons-resumo`,
        method: "GET",
      }),
      providesTags: (_result, _error, { eventoId }) => [
        { type: "CuponsResumo", id: String(eventoId) },
      ],
    }),
    getCupomUtilizacoesPaginated: build.query<
      any,
      IEventoPaginatedRequest & { cupomId: number; loginId?: number | null }
    >({
      query: ({ eventoId, cupomId, loginId, page, perPage, sortingField, sortingDir, search }) => ({
        url: `/eventos/${eventoId}/cupons/${cupomId}/utilizacoes`,
        params: {
          page,
          perPage,
          sortingDir,
          sortingField,
          search,
          ...(loginId != null && { loginId }),
        },
        method: "GET",
      }),
      providesTags: (_result, _error, { cupomId }) => [
        { type: "CupomUtilizacoes", id: String(cupomId) },
      ],
    }),
    postCupom: build.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: `/eventos/${body.EventoId}/cupons`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Cupons", id: window.localStorage.getItem("EventoId") },
        { type: "CuponsResumo", id: window.localStorage.getItem("EventoId") },
      ],
    }),
    patchCupom: build.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: `/eventos/${body.EventoId}/cupons/${body.Id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: () => [
        { type: "Cupons", id: window.localStorage.getItem("EventoId") },
        { type: "CuponsResumo", id: window.localStorage.getItem("EventoId") },
      ],
    }),
    deleteCupom: build.mutation<any, { eventoId: number; cupomId: number }>({
      query: ({ eventoId, cupomId }) => ({
        url: `/eventos/${eventoId}/cupons/${cupomId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [
        { type: "Cupons", id: window.localStorage.getItem("EventoId") },
        { type: "CuponsResumo", id: window.localStorage.getItem("EventoId") },
      ],
    }),
    postEvento: build.mutation<any, z.infer<typeof EventoFormSchema>>({
      query: (body) => ({
        url: `/eventos`,
        method: "POST",
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          syncEventoSalvoNaLista(
            dispatch,
            getState as () => RootState,
            body as unknown as Record<string, unknown>,
            data,
          );
        } catch {
          /* mutation falhou — store permanece inalterada */
        }
      },
      invalidatesTags: (_result, _error, body) => {
        const loginId = body?.LoginId;
        return [
          { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
          {
            type: "Eventos",
            id: window.localStorage.getItem("EventoId"),
          },
          { type: "Eventos", id: "AGENDA_PERIODO" },
          ...(loginId
            ? [{ type: "Eventos" as const, id: `limite-criacao-${loginId}` }]
            : []),
          // Sem id: invalida qualquer tag SelectEventos (lista home não depende do EventoId do localStorage).
          { type: "SelectEventos" },
          { type: "SelectEventos", id: SELECT_EVENTOS_LIST_TAG_ID },
        ];
      },
    }),
    finalizeWizardCadastro: build.mutation<
      { eventoId: number; configuracaoId: number },
      Record<string, unknown>
    >({
      query: (body) => ({
        url: `/eventos/cadastro-wizard/finalize`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, body) => {
        const loginId = body?.loginId;
        return [
          { type: "Configuracoes" },
          { type: "TiposEvento" },
          { type: "SelectEventos" },
          { type: "Eventos" },
          { type: "Eventos", id: "AGENDA_PERIODO" },
          ...(loginId
            ? [{ type: "Eventos" as const, id: `limite-criacao-${loginId}` }]
            : []),
        ];
      },
    }),
    postAdicional: build.mutation<any, z.infer<typeof AdicionalFormSchema>>({
      query: (body) => ({
        url: `/eventos/adicionais`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => {
        return [
          {
            type: "Adicionais",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    getLimiteCriacaoEventos: build.query<
      {
        eventosNoAno: number;
        limiteEventos: number | null;
        permitidoCriar: boolean;
        mensagem: string | null;
        orientacao: string | null;
      },
      { loginId: number }
    >({
      query: ({ loginId }) => ({
        url: `/eventos/limite-criacao`,
        params: { loginId },
        method: "GET",
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs.loginId})`,
      providesTags: (_result, _error, { loginId }) => [{ type: "Eventos", id: `limite-criacao-${loginId}` }],
    }),
    getTiposEvento: build.query<
      z.infer<typeof configuracaosModel>[],
      { loginId: number; omitWizardRascunhos?: boolean }
    >({
      query: ({ loginId, omitWizardRascunhos }) => ({
        url: `/eventos/tipos${omitWizardRascunhos ? "?omitWizardRascunhos=1" : ""}`,
        params: { loginId },
        method: "GET",
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.loginId},${queryArgs.omitWizardRascunhos ? "1" : "0"})`,
      providesTags: () => {
        return [
          {
            type: "TiposEvento",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    deleteEvento: build.mutation<any, number>({
      query: (id) => ({
        url: `/eventos/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
        {
          type: "Eventos",
          id: window.localStorage.getItem("EventoId"),
        },
        { type: "Eventos", id: "AGENDA_PERIODO" },
      ],
    }),
    // #730: exclusão pela listagem, bloqueada no backend se houver dado vinculado.
    deleteEventoVazio: build.mutation<any, number>({
      query: (id) => ({
        url: `/eventos/${id}/vazio`,
        method: "DELETE",
      }),
      invalidatesTags: () => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
        {
          type: "Eventos",
          id: window.localStorage.getItem("EventoId"),
        },
        { type: "Eventos", id: "AGENDA_PERIODO" },
      ],
    }),
    deleteAdicional: build.mutation<any, number>({
      query: (id) => ({
        url: `/eventos/adicionais/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => {
        return [
          {
            type: "Adicionais",
            id: window.localStorage.getItem("EventoId"),
          },
        ];
      },
    }),
    extractExcelEventos: build.mutation<
      any,
      IBasePaginatedRequestTipo & { filename?: string; campos?: string | string[] }
    >({
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const {
          filename,
          campos,
          tipo,
          emAndamento,
          apenasConcluidas,
          cursoId,
          semestreLetivo,
          status,
          search,
        } = args;
        const result = await baseQuery({
          url: `/eventos/datatable`,
          params: {
            extract: "excel",
            campos,
            filename,
            ...(tipo !== undefined && { tipo }),
            ...(emAndamento !== undefined && { emAndamento }),
            ...(apenasConcluidas !== undefined && { apenasConcluidas }),
            ...(cursoId != null && { cursoId }),
            ...(semestreLetivo ? { semestreLetivo } : {}),
            ...(status != null && status !== "" && { status }),
            ...(search ? { search } : {}),
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),
    clonarEvento: build.mutation<any, number>({
      query: (id) => ({
        url: `/eventos/clonar/${id}`,
        method: "POST",
      }),
      invalidatesTags: () => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
        {
          type: "Eventos",
          id: window.localStorage.getItem("EventoId"),
        },
        { type: "Eventos", id: "AGENDA_PERIODO" },
        {
          type: "SelectEventos",
          id: window.localStorage.getItem("EventoId"),
        },
      ],
    }),
    getProdutos: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/produtos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: RESOURCES.PRODUTOS_EVENTO,
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetProdutos,
        );
      },
    }),
    getGruposEvento: build.query<any, number>({
      // Pré-carga de metadados do evento selecionado (onda do eventosListener): falha aqui
      // (ex.: EventoId salvo de outra igreja/evento público) não deve virar toast global.
      extraOptions: skipGlobalErrorToast,
      query: (eventoId) => ({
        url: `/eventos/grupos/${eventoId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, eventoId) => {
        return [
          {
            type: RESOURCES.GRUPOS_EVENTO,
            id: String(eventoId),
          },
        ];
      },
      async onQueryStarted(eventoId, { dispatch, queryFulfilled, getState }) {
        await syncSliceIfCurrentEvento(
          eventoId,
          queryFulfilled,
          getState,
          dispatch,
          SetGrupos,
        );
      },
    }),
    postBackground: build.mutation<
      any,
      {
        Id: number;
        Titulo: string;
        BackgroundId?: number;
        backgroundFile?: File;
      }
    >({
      extraOptions: skipGlobalErrorToast,
      queryFn: async (body, api, extraOptions, baseQuery) => {
        const formData = new FormData();
        formData.append("Id", body.Id.toString());
        formData.append("Titulo", body.Titulo);
        if (body.BackgroundId) {
          formData.append("BackgroundId", body.BackgroundId.toString());
        }
        if (body.backgroundFile) {
          const prepared = await prepareImageForUpload(body.backgroundFile);
          formData.append("background", prepared);
        }

        const result = await baseQuery({
            url: `/eventos/background`,
            method: "POST",
            body: formData,
          });
        if (result.error) {
          void postLog({
            Tipo: "Arquivos_Falhou_Upload",
            Objeto: {
              acao: "upload",
              contexto: "post_background",
              status: result.error.status,
            },
          });
        }
        return result;
      },
      invalidatesTags: () => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
        {
          type: "Eventos",
          id: window.localStorage.getItem("EventoId"),
        },
        {
          type: "SelectEventos",
          id: window.localStorage.getItem("EventoId"),
        },
      ],
    }),
    getTurmasEmAndamento: build.query<any, void>({
      query: () => ({
        url: `/eventos/turmas-em-andamento`,
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "TurmasEmAndamento",
            id: "LIST",
          },
        ];
      },
    }),
    getPerfilTurma: build.query<
      any,
      number | { eventoId: number; loginId?: number }
    >({
      query: (arg) => {
        const eventoId =
          typeof arg === "object" ? arg.eventoId : arg;
        const loginId =
          typeof arg === "object" ? arg.loginId : undefined;
        return {
          url: `/eventos/perfil-turma/${eventoId}`,
          method: "GET",
          params: loginId != null ? { loginId } : undefined,
        };
      },
      providesTags: (result, error, arg) => {
        const eventoId =
          typeof arg === "object" ? arg.eventoId : arg;
        return [
          {
            type: "PerfilTurma",
            id: eventoId,
          },
        ];
      },
    }),
    getAlunosUnicosPorStatus: build.query<
      any,
      number | { status: number; semestreLetivo?: string }
    >({
      query: (arg) => {
        const status = typeof arg === "object" ? arg.status : arg;
        const semestreLetivo =
          typeof arg === "object" ? arg.semestreLetivo : undefined;
        return {
          url: `/eventos/alunos-unicos-por-status`,
          params: {
            status,
            ...(semestreLetivo ? { semestreLetivo } : {}),
          },
          method: "GET",
        };
      },
      providesTags: (result, error, arg) => {
        const status = typeof arg === "object" ? arg.status : arg;
        const semestreLetivo =
          typeof arg === "object" ? arg.semestreLetivo : undefined;
        return [
          {
            type: "AlunosUnicosPorStatus",
            id: semestreLetivo ? `${status}-${semestreLetivo}` : status,
          },
        ];
      },
    }),
    getAniversariantesDoDia: build.query<
      {
        data: Array<{
          Id: number;
          EquipanteId?: number;
          Nome: string;
          Apelido: string;
          DataNascimento: string;
          Email: string;
          Fone: string;
          Idade: number;
        }>;
        total: number;
      },
      { loginId: number; dataInicial?: string; dataFinal?: string; apenasComTurmasAtivas?: boolean }
    >({
      query: ({ loginId, dataInicial, dataFinal, apenasComTurmasAtivas }) => ({
        url: `/eventos/aniversariantes-do-dia`,
        params: { loginId, dataInicial, dataFinal, apenasComTurmasAtivas },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "AniversariantesDoDia",
            id: "LIST",
          },
        ];
      },
    }),
    getAniversarioPushConfig: build.query<
      { ativo: boolean; mensagem: string | null; mensagemPadrao: string },
      { loginId: number }
    >({
      extraOptions: skipGlobalErrorToast,
      query: ({ loginId }) => ({
        url: `/igreja/aniversario-push`,
        params: { loginId },
        method: "GET",
      }),
      providesTags: () => [{ type: RESOURCES.ANIVERSARIANTES, id: "PUSH-CONFIG" }],
    }),

    updateAniversarioPushConfig: build.mutation<
      { ativo: boolean; mensagem: string | null; mensagemPadrao: string },
      { LoginId: number; Ativo?: boolean; Mensagem?: string | null }
    >({
      extraOptions: skipGlobalErrorToast,
      query: (body) => ({
        url: `/igreja/aniversario-push`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: () => [{ type: RESOURCES.ANIVERSARIANTES, id: "PUSH-CONFIG" }],
    }),

    getAniversariantesDoDiaTodos: build.query<
      {
        data: Array<{
          EquipanteId: number;
          Nome: string;
          Apelido: string;
          DataNascimento: string;
          Email: string;
          Fone: string;
          Idade: number;
        }>;
        total: number;
      },
      { loginId: number; dataInicial?: string; dataFinal?: string }
    >({
      query: ({ loginId, dataInicial, dataFinal }) => ({
        url: `/eventos/aniversariantes-do-dia-todos`,
        params: { loginId, dataInicial, dataFinal },
        method: "GET",
      }),
      providesTags: () => {
        return [
          {
            type: "AniversariantesDoDiaTodos",
            id: "LIST",
          },
        ];
      },
    }),

    extractExcelAniversariantesHome: build.mutation<
      void,
      {
        variant: "ensino" | "igreja";
        loginId: number;
        filename: string;
        campos: string;
        dataInicial?: string;
        dataFinal?: string;
        apenasComTurmasAtivas?: boolean;
      }
    >({
      queryFn: async (
        { variant, loginId, filename, campos, dataInicial, dataFinal, apenasComTurmasAtivas },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        const url =
          variant === "ensino"
            ? `/eventos/aniversariantes-do-dia`
            : `/eventos/aniversariantes-do-dia-todos`;
        const result = await baseQuery({
          url,
          params: {
            loginId,
            dataInicial,
            dataFinal,
            apenasComTurmasAtivas,
            extract: "excel",
            campos,
            filename,
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),

    // IA - Perguntar sobre Eventos
    perguntarIA: build.mutation<PerguntarIAEventoResponse, { eventoId: number; loginId: number; body: PerguntarIAEventoDto }>({
      query: ({ eventoId, loginId, body }) => ({
        url: `/events/${eventoId}/ask`,
        method: "POST",
        params: {
          loginId,
        },
        body,
      }),
    }),
    // IA - Sugestões de Perguntas
    getSugestoes: build.query<SugestoesResponse, { eventoId: number; loginId: number; limit?: number }>({
      query: ({ eventoId, loginId, limit = 10 }) => ({
        url: `/events/${eventoId}/suggestions`,
        method: "GET",
        params: {
          loginId,
          limit,
        },
      }),
    }),
    // IA - Histórico de Perguntas
    getHistoricoPerguntas: build.query<HistoricoPerguntasResponse, { loginId: number; eventoId?: number }>({
      query: ({ loginId, eventoId }) => ({
        url: `/events/perguntas`,
        method: "GET",
        params: {
          loginId,
          ...(eventoId && { eventoId }),
        },
      }),
      providesTags: () => [
        {
          type: "HistoricoPerguntas",
          id: "LIST",
        },
      ],
    }),
    getMultiIngressosConfig: build.query<
      MultiIngressosConfig,
      { eventoId: number; loginId: number }
    >({
      query: ({ eventoId, loginId }) => ({
        url: `/eventos/${eventoId}/multi-ingressos/config`,
        params: { loginId },
      }),
      providesTags: (_result, _error, { eventoId }) => [
        { type: "Eventos", id: `multi-ingressos-${eventoId}` },
      ],
    }),
    updateMultiIngressosConfig: build.mutation<
      MultiIngressosConfig,
      { eventoId: number; body: UpdateMultiIngressosConfigPayload }
    >({
      query: ({ eventoId, body }) => ({
        url: `/eventos/${eventoId}/multi-ingressos/config`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { eventoId }) => [
        { type: "Eventos", id: EVENTOS_DATATABLE_LIST_TAG_ID },
        { type: "Eventos", id: `multi-ingressos-${eventoId}` },
      ],
    }),
    reservarMultiIngressos: build.mutation<
      ReservarMultiIngressoResponse,
      { EventoId: number; LoginId: number; Quantidade: number }
    >({
      query: (body) => ({
        url: `/participantes/multi-ingressos/reservar`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetCamposQuery,
  useGetEquipesFormularioQuery,
  useGetEquipesEventosQuery,
  useGetEtiquetasQuery,
  useGetEventosQuery,
  useGetEventoQuery,
  useLazyGetEventoQuery,
  useGetCirculosQuery,
  useGetVoluntariosQuery,
  useGetVoluntariosPadrinhosQuery,
  useGetQuartosVoluntariosQuery,
  useGetVoluntariosQuartosQuery,
  useGetVoluntariosCaronasQuery,
  useGetCaronasQuery,
  usePostEventoMutation,
  useFinalizeWizardCadastroMutation,
  useDeleteEventoMutation,
  useDeleteEventoVazioMutation,
  useGetEventosPaginatedQuery,
  useGetEventosAgendaPeriodoQuery,
  useLazyGetEventosAgendaPeriodoQuery,
  useGetTurmasDisponiveisPerfilQuery,
  useGetLimiteCriacaoEventosQuery,
  useLazyGetLimiteCriacaoEventosQuery,
  useGetTiposEventoQuery,
  useExtractExcelEventosMutation,
  useClonarEventoMutation,
  useGetEventosConfiguracaoQuery,
  useGetResumoQuery,
  useGetAdicionaisPaginatedQuery,
  usePostAdicionalMutation,
  useDeleteAdicionalMutation,
  useGetCuponsPaginatedQuery,
  useGetCuponsResumoQuery,
  useGetCupomUtilizacoesPaginatedQuery,
  useGetIgrejasEventoQuery,
  useGetEventoByIdentificadorQuery,
  useGetVoluntariosDirigenteQuery,
  useLazyGetEventoByIdentificadorQuery,
  useGetProdutosQuery,
  useGetReunioesQuery,
  useGetGruposEventoQuery,
  usePostBackgroundMutation,
  useGetCirculosWithDetailsQuery,
  useGetTurmasEmAndamentoQuery,
  useGetPerfilTurmaQuery,
  useGetAlunosUnicosPorStatusQuery,
  useGetAniversariantesDoDiaQuery,
  useGetAniversarioPushConfigQuery,
  useUpdateAniversarioPushConfigMutation,
  useGetAniversariantesDoDiaTodosQuery,
  useExtractExcelAniversariantesHomeMutation,
  usePerguntarIAMutation,
  useGetSugestoesQuery,
  useGetHistoricoPerguntasQuery,
  useGetMultiIngressosConfigQuery,
  useUpdateMultiIngressosConfigMutation,
  useReservarMultiIngressosMutation,
} = eventosAPI;
